import type { QuizQuestion, Section } from 'components/BuildVsBuyQuiz/config/types';

/**
 * Default sections for Build vs Buy quiz
 * Configurable from Builder.io
 */
export const DEFAULT_SECTIONS: Section[] = [
  {
    id: 'business',
    displayName: 'Business & customer impact',
  },
  {
    id: 'resources',
    displayName: 'Resources',
  },
  {
    id: 'technical',
    displayName: 'Technical needs & capabilities',
  },
];

/**
 * Hardcoded 13 questions for the Build vs Buy quiz
 *
 * Scoring: Blue = Build, Orange = Buy
 *
 * Section breakdown:
 * - business: Q1-Q4 (4 questions)
 * - resources: Q5-Q7 (3 questions)
 * - technical: Q8-Q13 (6 questions)
 */
export const QUIZ_QUESTIONS: QuizQuestion[] = [
  // Business & customer impact (4 questions)
  {
    id: 'q1_primary_differentiator',
    section: 'business',
    question: 'Are integrations a primary competitive differentiator?',
    blueLabel: 'Yes',
    orangeLabel: 'No',
  },
  {
    id: 'q2_deals_require_integrations',
    section: 'business',
    question: 'What percentage of deals require integrations to close?',
    blueLabel: 'Less than 10%',
    orangeLabel: '10% or more',
  },
  {
    id: 'q3_customer_request_frequency',
    section: 'business',
    question: 'How frequently do customers request new integrations?',
    blueLabel: 'Monthly',
    orangeLabel: 'Yearly',
  },
  {
    id: 'q4_customer_scale',
    section: 'business',
    question: 'Will more than 100 customers use your integrations?',
    blueLabel: 'Yes',
    orangeLabel: 'No',
  },

  // Resources (3 questions)
  {
    id: 'q5_engineering_capacity',
    section: 'resources',
    question: 'Is your engineering team at capacity with your core product?',
    blueLabel: 'Yes',
    orangeLabel: 'No',
  },
  {
    id: 'q6_team_size',
    section: 'resources',
    question: 'Do you have 20 or more engineers?',
    blueLabel: 'Yes',
    orangeLabel: 'No',
  },
  {
    id: 'q7_timeline_urgency',
    section: 'resources',
    question: 'Do you need integrations live in three months or less?',
    blueLabel: 'Yes',
    orangeLabel: 'No',
  },

  // Technical needs & capabilities (6 questions)
  {
    id: 'q8_complex_workflows',
    section: 'technical',
    question: 'Does your integration logic require complex business rules or branching workflows?',
    blueLabel: 'Yes',
    orangeLabel: 'No',
  },
  {
    id: 'q9_platform_expertise',
    section: 'technical',
    question: 'Do you have deep integration platform expertise (OAuth, webhooks, multi-tenancy, etc.)?',
    blueLabel: 'Yes',
    orangeLabel: 'No',
  },
  {
    id: 'q10_data_residency',
    section: 'technical',
    question: 'Do you have strict data residency requirements that prohibit third-party processing?',
    blueLabel: 'Yes',
    orangeLabel: 'No',
  },
  {
    id: 'q11_architecture_control',
    section: 'technical',
    question: 'Do you need complete control over integration architecture?',
    blueLabel: 'Yes',
    orangeLabel: 'No',
  },
  {
    id: 'q12_compliance_requirements',
    section: 'technical',
    question: 'Do you need SOC 2, GDPR, HIPAA, or other compliance certifications for integration infrastructure?',
    blueLabel: 'Yes',
    orangeLabel: 'No',
  },
  {
    id: 'q13_multi_tenant_deployment',
    section: 'technical',
    question: 'Do you need the same integration deployed to multiple customers with different configurations?',
    blueLabel: 'Yes',
    orangeLabel: 'No',
  },
];

/**
 * Get display name for a section
 * Looks up section in sections array
 * Falls back to capitalized section ID if not found
 *
 * @param sectionId - The section identifier (e.g., "business", "train")
 * @param sections - Array of section definitions
 * @returns Display name for the section
 */
export const getSectionDisplayName = (sectionId: string, sections: Section[]): string => {
  const section = sections.find(s => s.id === sectionId);

  if (section?.displayName) {
    return section.displayName;
  }

  // Fallback: capitalize first letter
  return sectionId.charAt(0).toUpperCase() + sectionId.slice(1);
};

/**
 * Calculate question counts per section dynamically
 * Works with any sections (default or custom)
 *
 * @param questions - Array of quiz questions
 * @returns Object with section keys and their question counts
 */
export const calculateSectionCounts = (questions: QuizQuestion[]): Record<string, number> => {
  const counts: Record<string, number> = {};

  questions.forEach(question => {
    const section = question.section;
    counts[section] = (counts[section] || 0) + 1;
  });

  return counts;
};

/**
 * Get all unique sections from questions array
 * Maintains order of first appearance
 *
 * @param questions - Array of quiz questions
 * @returns Array of unique section keys in order
 */
export const getUniqueSections = (questions: QuizQuestion[]): string[] => {
  const seen = new Set<string>();
  const sections: string[] = [];

  questions.forEach(question => {
    if (!seen.has(question.section)) {
      seen.add(question.section);
      sections.push(question.section);
    }
  });

  return sections;
};
